/**
 * Route preview sheet: destination, alternatives with traffic-aware times AND
 * the things the ETA hides (how many traffic lights, how many turns, tolls),
 * avoid-toggles that re-route immediately, vehicle indicator, GO button.
 *
 * The traffic-light count is here because the driver pointed out that it
 * changes which route is actually better: a road with eighteen fewer signals
 * is a calmer, more predictable drive even when it shows a minute or two
 * longer, and the routing engine will never surface that trade on its own.
 *
 * Above the list sits the AI advice card when the model has an opinion worth
 * showing - see ai/advisor.ts.
 */

import type { Place, Route } from '../core/types';
import type { RouteStats } from '../nav/routeStats';
import type { RouteAdvice } from '../ai/advisor';
import { isDemanding, worstSlope, type ElevationProfile } from '../nav/elevation';
import type { RouteRoughness } from '../nav/roughMap';
import { MOSTLY_BIG, type RoadWidthProfile } from '../nav/bigRoads';
import type { FerryPlan } from '../nav/ferry';
import type { PttGapStats } from '../nav/fuelStops';
import { formatClock, formatDistance, formatDuration, formatEta, routeLetter } from '../core/format';
import { summariseRoute } from '../nav/directions';
import { store } from '../core/store';
import {
  activeVehicle,
  autoPreviewHeight,
  PREVIEW_HEIGHT_COMPACT,
  PREVIEW_HEIGHT_MAX,
  PREVIEW_HEIGHT_MIN,
} from '../core/settings';
import { paceDuration } from '../nav/paceModel';
import { t } from '../core/i18n';

export interface PreviewState {
  destination: Place;
  routes: Route[];
  stats: RouteStats[];
  selectedIndex: number;
  loading: boolean;
  error: string | null;
  isSaved?: boolean;
  advice?: RouteAdvice | null;
  adviceLoading?: boolean;
  /** Gradient analysis of the selected route, for the active vehicle. */
  elevation?: ElevationProfile | null;
  vehicleName?: string;
  /** Name of a planned start point, or null when starting from the GPS fix. */
  originName?: string | null;
  /** Road surface on this route, learned from our own past drives. */
  roughness?: RouteRoughness | null;
  /** Road surface per option, so a rough route can be spotted before choosing. */
  roughPerRoute?: Array<RouteRoughness | null>;
  /** How much of each option runs on dual carriageway or better. */
  widthPerRoute?: Array<RoadWidthProfile | null>;
  /** Ferry sailing chosen for each option, when the route uses a crossing. */
  ferryPerRoute?: Array<FerryPlan | null>;
  /** PTT spacing along each option; empty while the searches are in flight. */
  pttPerRoute?: Array<PttGapStats | null>;
}

export class RoutePreview {
  readonly el: HTMLElement;

  constructor(
    parent: HTMLElement,
    private actions: {
      onGo: () => void;
      onCancel: () => void;
      onSelectRoute: (index: number) => void;
      onSwitchVehicle: () => void;
      onSavePlace: () => void;
      onPickOrigin: () => void;
      onClearOrigin: () => void;
      onDismissAdvice: () => void;
      /** Pick the option that dodges the most traffic while staying easy. */
      onAvoidTraffic: () => void;
    },
  ) {
    this.el = document.createElement('div');
    this.el.className = 'sheet sheet--preview';
    this.el.hidden = true;
    parent.appendChild(this.el);
    this.applyHeight(store.get().settings.previewHeight);
    this.enableResize();
  }

  hide(): void {
    this.el.hidden = true;
  }

  /**
   * Push the chosen height into CSS and decide whether type should compact.
   *
   * Kept as a cap (`max-height`) rather than a fixed height so a sheet with
   * only one route still hugs its content instead of leaving a gap over the
   * map; dragging sets how much room it is ALLOWED, which is the thing that
   * actually matters on a short screen.
   */
  private applyHeight(vh: number): void {
    // 0 = never dragged, so pick a height that suits this screen.
    const wanted = vh > 0 ? vh : autoPreviewHeight(window.innerHeight);
    const clamped = Math.min(PREVIEW_HEIGHT_MAX, Math.max(PREVIEW_HEIGHT_MIN, wanted));
    this.el.style.setProperty('--preview-h', `${clamped}vh`);
    this.el.classList.toggle('is-compact', clamped < PREVIEW_HEIGHT_COMPACT);
  }

  /**
   * Drag the grab handle to resize.
   *
   * The handle was already drawn at the top of the sheet and looked draggable
   * without doing anything; this makes it mean what it looks like. Dragging UP
   * (a negative dy) makes the sheet taller, which is the direction the handle
   * physically moves. Pointer events cover touch, pen and mouse in one path,
   * and capture keeps the drag alive when the finger slides off the 44px strip.
   */
  private enableResize(): void {
    let startY = 0;
    let startVh = 0;
    let dragging = false;

    const onDown = (ev: PointerEvent): void => {
      const handle = (ev.target as HTMLElement).closest('.sheet__grab');
      if (!handle) return;
      dragging = true;
      startY = ev.clientY;
      // Drag continues from whatever is on screen, including an auto height.
      startVh = parseFloat(this.el.style.getPropertyValue('--preview-h')) ||
        autoPreviewHeight(window.innerHeight);
      (handle as HTMLElement).setPointerCapture(ev.pointerId);
      this.el.classList.add('is-resizing');
      ev.preventDefault();
    };
    const onMove = (ev: PointerEvent): void => {
      if (!dragging) return;
      const dy = startY - ev.clientY; // up = taller
      this.applyHeight(startVh + (dy / window.innerHeight) * 100);
      ev.preventDefault();
    };
    const onUp = (): void => {
      if (!dragging) return;
      dragging = false;
      this.el.classList.remove('is-resizing');
      // Read back what applyHeight actually settled on, so the stored value is
      // already clamped and a later render cannot drift from what is on screen.
      const px = parseFloat(this.el.style.getPropertyValue('--preview-h'));
      if (Number.isFinite(px)) store.patchSettings({ previewHeight: Math.round(px) });
    };

    this.el.addEventListener('pointerdown', onDown);
    this.el.addEventListener('pointermove', onMove);
    this.el.addEventListener('pointerup', onUp);
    this.el.addEventListener('pointercancel', onUp);
    // Double-tapping the handle restores the default, so a sheet dragged to a
    // useless size is always one gesture from being usable again.
    this.el.addEventListener('dblclick', (ev) => {
      if (!(ev.target as HTMLElement).closest('.sheet__grab')) return;
      this.applyHeight(0);
      store.patchSettings({ previewHeight: 0 });
    });
  }

  render(state: PreviewState): void {
    const s = store.get().settings;
    const vehicle = activeVehicle(s);
    const { destination, routes, stats, selectedIndex, loading, error } = state;
    this.el.hidden = false;
    // Stay in step with the stored value - it can change from elsewhere (a
    // settings reset, another tab) and the sheet must not drift from it.
    this.applyHeight(s.previewHeight);

    // Rebuilding innerHTML resets the scroll box to the top, which yanked the
    // list away every time an option was tapped - picking B, then comparing C,
    // meant scrolling back down each time. Carry the offset across the render.
    const prevScroll = this.el.querySelector('.sheet__scroll')?.scrollTop ?? 0;

    // Which option has the fewest signals, so it can be badged even when it
    // isn't the fastest.
    const calmest =
      routes.length > 1 && stats.length === routes.length
        ? stats.reduce(
            (best, cur, i) => (cur.trafficSignals < stats[best].trafficSignals ? i : best),
            0,
          )
        : -1;

    const routesHtml = loading
      ? `<div class="sheet__loading"><span class="spinner"></span> ${t('preview.finding')}</div>`
      : error
        ? `<div class="sheet__error">${escapeHtml(error)}</div>`
        : routes
            .map((r, i) => {
              const selected = i === selectedIndex;
              const hasTraffic = r.duration_typical != null && r.duration_typical > 0;
              const delay = hasTraffic ? r.duration - (r.duration_typical ?? r.duration) : 0;
              const st = stats[i];
              // Times shown are the driver's own pace plus red-light time, not
              // the router's generic-vehicle estimate. A ferry wait (arrive an
              // hour early, wait for the sailing) goes in too - a "faster"
              // road that misses the boat is not faster.
              const ferry = state.ferryPerRoute?.[i] ?? null;
              const shown = paceDuration(r) + (ferry?.waitSeconds ?? 0);
              const atLights = r._paceSignalSeconds ?? 0;
              const showCalmest = i === calmest && st && st.trafficSignals > 0 && calmest !== 0;
              return `
              <button class="route-opt ${selected ? 'is-selected' : ''}" data-i="${i}">
                <span class="route-opt__letter" aria-hidden="true">${routeLetter(i)}</span>
                <span class="route-opt__time">${formatDuration(shown)}
                  ${r._tag ? `<em class="route-opt__tag route-opt__tag--${r._tag}">${t(`route.${r._tag}`)}</em>` : ''}
                  ${showCalmest ? `<em class="route-opt__tag route-opt__tag--calm">${t('stats.calmest')}</em>` : ''}
                  ${r._widest && r._tag !== 'big-roads' ? `<em class="route-opt__tag route-opt__tag--big-roads">${t('route.big-roads')}</em>` : ''}
                </span>
                <span class="route-opt__meta">
                  <span>${formatDistance(r.distance, s.units)} · ${escapeHtml(summariseRoute(r))}</span>
                  ${
                    delay > 180
                      ? `<span class="route-opt__delay">${t('preview.traffic', { d: formatDuration(delay) })}</span>`
                      : hasTraffic
                        ? `<span class="route-opt__clear">${t('preview.clear')}</span>`
                        : ''
                  }
                </span>
                ${st ? routeStatsRow(st, atLights) : ''}
                ${widthRow(state.widthPerRoute?.[i] ?? null, s.units)}
                ${roughRow(state.roughPerRoute?.[i] ?? null, s.units)}
                ${ferryRow(ferry)}
                ${pttRow(state.pttPerRoute?.[i] ?? null, s.units)}
                <span class="route-opt__eta">${t('preview.eta', { t: formatEta(shown) })}</span>
              </button>`;
            })
            .join('');

    this.el.innerHTML = `
      <div class="sheet__grab"></div>
      <div class="sheet__dest">
        <div class="sheet__dest-text">
          <div class="sheet__dest-name">${escapeHtml(destination.name)}</div>
          <div class="sheet__dest-addr">${escapeHtml(destination.address)}</div>
        </div>
        <button class="icon-btn sheet__save ${state.isSaved ? 'is-saved' : ''}" data-act="save"
                aria-label="${state.isSaved ? 'Saved for the fleet' : 'Save for the fleet'}" ${state.isSaved ? 'disabled' : ''}>
          ${STAR_SVG}
        </button>
      </div>
      <div class="sheet__scroll">
      ${gradeWarningHtml(state)}
      ${roughWarningHtml(state)}
      ${this.adviceHtml(state)}
      <button class="origin-row" data-act="origin">
        <span class="origin-row__label">${t('preview.from')}</span>
        <span class="origin-row__value ${state.originName ? 'is-set' : ''}">
          ${escapeHtml(state.originName ?? t('preview.here'))}
        </span>
        ${state.originName ? `<span class="origin-row__clear" data-act="clear-origin">${t('preview.reset')}</span>` : ''}
      </button>
      <div class="sheet__routes">${routesHtml}</div>
      </div>
      <div class="sheet__chips">
        <!-- Avoid-tolls and avoid-motorways moved to Settings > Route. They are
             preferences set once, not decisions made at a junction, and on the
             sheet they cost a row of map the driver would rather see. -->
        ${
          routes.length > 1 && !loading && !error
            ? `<button class="chip chip--traffic" data-act="avoid-traffic">
                 ${TRAFFIC_SVG}${t('traffic.chip')}
               </button>`
            : ''
        }
        <!-- Reads as the CURRENT vehicle, with a caret to say it opens a list -
             the old chip cycled on tap, so the name on it was ambiguous. -->
        <button class="chip chip--vehicle" data-act="vehicle"
                aria-label="${escapeHtml(vehicle.name)} - ${t('vehicle.choose')}">
          ${vehicle.kind === 'truck' ? TRUCK_SVG : CAR_SVG}
          ${escapeHtml(vehicle.name)}
          <svg class="chip__caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10 L12 15 L17 10"
            fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
            stroke-linejoin="round"/></svg>
        </button>
      </div>
      <div class="sheet__actions">
        <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
        <button class="btn btn--primary btn--go" data-act="go" ${loading || !!error || !routes.length ? 'disabled' : ''}>
          ${GO_SVG} ${t('preview.start')}
        </button>
      </div>`;

    const scroller = this.el.querySelector<HTMLElement>('.sheet__scroll');
    if (scroller && prevScroll > 0) scroller.scrollTop = prevScroll;

    this.el.querySelectorAll<HTMLButtonElement>('.route-opt').forEach((b) =>
      b.addEventListener('click', () => this.actions.onSelectRoute(Number(b.dataset.i))),
    );
    this.el.querySelector('[data-act=vehicle]')!.addEventListener('click', () => this.actions.onSwitchVehicle());
    this.el
      .querySelector('[data-act=avoid-traffic]')
      ?.addEventListener('click', () => this.actions.onAvoidTraffic());
    this.el.querySelector('[data-act=save]')?.addEventListener('click', () => this.actions.onSavePlace());
    this.el.querySelector('[data-act=cancel]')!.addEventListener('click', () => this.actions.onCancel());
    this.el.querySelector('[data-act=go]')?.addEventListener('click', () => this.actions.onGo());
    this.el.querySelector('[data-act=origin]')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('[data-act=clear-origin]')) {
        this.actions.onClearOrigin();
        return;
      }
      this.actions.onPickOrigin();
    });
    this.el
      .querySelector('[data-act=use-advice]')
      ?.addEventListener('click', () =>
        this.actions.onSelectRoute(Number(state.advice?.pick ?? selectedIndex)),
      );
    this.el
      .querySelector('[data-act=dismiss-advice]')
      ?.addEventListener('click', () => this.actions.onDismissAdvice());
  }

  private adviceHtml(state: PreviewState): string {
    if (state.adviceLoading) {
      return `<div class="advice advice--loading"><span class="spinner"></span> ${t('ai.thinking')}</div>`;
    }
    const a = state.advice;
    if (!a) return '';
    const already = a.pick === state.selectedIndex;
    return `
      <div class="advice advice--${a.confidence}">
        <div class="advice__head">
          <span class="advice__badge">${t('ai.badge')}</span>
          <button class="advice__close" data-act="dismiss-advice" aria-label="${t('ai.dismiss')}">
            <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="advice__headline">${escapeHtml(a.headline)}</div>
        <div class="advice__reason">${escapeHtml(a.reason)}</div>
        ${
          a.tips.length
            ? `<ul class="advice__tips">${a.tips.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul>`
            : ''
        }
        ${
          already
            ? ''
            : `<button class="btn btn--small btn--primary advice__use" data-act="use-advice">${t('ai.use')}</button>`
        }
      </div>`;
  }
}

/**
 * Gradient warning. Only appears when the climb or descent is genuinely hard
 * for the vehicle currently selected - a hill that is nothing to a car can be
 * a low-gear crawl or a brake-heat problem for a loaded truck, so the same
 * route says different things depending on what you are driving.
 */
function gradeWarningHtml(state: PreviewState): string {
  const p = state.elevation;
  if (!p || !isDemanding(p)) return '';
  const worst = worstSlope(p);
  if (!worst) return '';

  const km = ((worst.to - worst.from) / 1000).toFixed(1);
  const pct = Math.abs(worst.grade).toFixed(0);
  const climbing = worst.climb;
  const head = state.vehicleName
    ? t(climbing ? 'grade.climbFor' : 'grade.descentFor', {
        pct,
        km,
        name: escapeHtml(state.vehicleName),
      })
    : '';
  const body = p.blocking ? t('grade.blocking') : t('grade.hard');

  return `<div class="grade-warning ${p.blocking ? 'grade-warning--blocking' : ''}" role="alert">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 19 L14 6 L22 19 Z" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
      <span><strong>${head}</strong>${body}
        ${t('grade.ascent', { m: String(p.totalAscent) })}</span>
    </div>`;
}

/**
 * Warn about surfaces we already know are bad.
 *
 * Only speaks up about roads this fleet has actually driven - an unknown road
 * is reported as nothing at all rather than as "fine", because silence and
 * a clean bill of health are very different claims.
 */
function roughWarningHtml(state: PreviewState): string {
  const r = state.roughness;
  if (!r || !r.known || r.metres < 400) return '';
  const severity = r.worst >= 55 ? t('rough.bad') : t('rough.moderate');
  return `<div class="grade-warning grade-warning--rough" role="note">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 17 L6 11 L10 15 L14 7 L18 13 L22 9"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span><strong>${t('rough.title')}</strong>
        ${t('rough.onRoute', { d: formatDistance(r.metres, store.get().settings.units) })} - ${severity}.</span>
    </div>`;
}

/** The line the ETA doesn't tell you: stops, turns, tolls. */

/**
 * How wide the roads are on this option.
 *
 * Shown on every route, not just the big-roads one, because the number is only
 * useful as a comparison - "82% dual" means nothing until you can see that the
 * alternative is 40%. The worst single-carriageway run is called out alongside
 * it, since a good average can hide a long stretch of no overtaking, which for
 * a loaded truck is the thing that actually matters.
 */
function widthRow(w: RoadWidthProfile | null, units: 'metric' | 'imperial'): string {
  if (!w) return '';
  const pct = Math.round(w.bigFraction * 100);
  if (pct < 12) return ''; // a town route; the figure is just noise
  const good = w.bigFraction >= MOSTLY_BIG;
  const label = pct >= 99 ? t('width.allBig') : t('width.big', { p: String(pct) });

  // Motorway called out separately: no lights, no junctions, nothing pulling
  // out of a soi. Two routes with the same "dual or better" figure can be very
  // different drives depending on how much of it is grade-separated.
  const mw = Math.round(w.motorwayFraction * 100);
  const motorway = mw >= 8 ? ` · ${t('width.motorway', { p: String(mw) })}` : '';

  // Call out the worst detour off the big roads whenever it is long enough to
  // change the character of the drive, not only on the good routes.
  const worst =
    w.worstSmallRun > 5000
      ? ` · ${t('width.smallRun', { d: formatDistance(w.worstSmallRun, units) })}`
      : '';
  return `<span class="route-opt__width ${good ? 'is-good' : ''}">
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3 V21 M20 3 V21 M12 4 v3 M12 10.5 v3 M12 17 v3"
      fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
    ${label}${motorway}${worst}</span>`;
}

/**
 * Rough-surface warning on the option itself.
 *
 * The driver asked for a bad surface to count against a route rather than be
 * discovered on it. The ranking penalty lives in nav/roughMap.ts; this is the
 * visible half, so the trade is legible instead of mysterious.
 */
function roughRow(r: RouteRoughness | null, units: 'metric' | 'imperial'): string {
  if (!r || !r.known || !r.metres) return '';
  const severe = r.worst >= 60;
  return `<span class="route-opt__rough ${severe ? 'is-severe' : ''}">
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 17 L6 12 L9 15 L13 8 L17 14 L20 11 L22 17"
      fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
      stroke-linejoin="round"/></svg>
    ${t('rough.onRoute', { d: formatDistance(r.metres, units) })}</span>`;
}

/**
 * The sailing this option is planned around: which ferry, when it leaves, and
 * when to be at the pier (an hour before - see nav/ferry.ts). Says so LOUDLY
 * when no configured operator takes the current vehicle, because the truck
 * arriving at a pier no ferry will board is the worst possible discovery.
 */
function ferryRow(plan: FerryPlan | null): string {
  if (!plan) return '';
  if (!plan.operator) {
    return `<span class="route-opt__ferry is-blocked">${FERRY_SVG}
      ${t('ferry.noVehicle', { name: plan.crossingName })}</span>`;
  }
  const parts = [
    t('ferry.sailing', {
      name: plan.operator.name,
      time: formatClock(plan.departureAt),
    }),
    t('ferry.beThere', { time: formatClock(plan.beAtPierBy), pier: plan.pierName }),
  ];
  if (plan.nextDay) parts.push(t('ferry.nextDay'));
  return `<span class="route-opt__ferry ${plan.nextDay ? 'is-blocked' : ''}">${FERRY_SVG}
    ${parts.join(' · ')}</span>`;
}

/**
 * How far apart the PTTs are on this option - the number that lets fuel range
 * pick the road. Absent (not "0") while the searches are still in flight; a
 * route where NO PTT was found is flagged in warning colour, since for the
 * truck that is as route-deciding as a rough road.
 */
function pttRow(g: PttGapStats | null, units: 'metric' | 'imperial'): string {
  if (!g) return '';
  if (g.count === 0) {
    return `<span class="route-opt__fuelgap is-warn">${PUMP_SVG} ${t('ptt.none')}</span>`;
  }
  if (g.count === 1) {
    return `<span class="route-opt__fuelgap">${PUMP_SVG} ${t('ptt.one')}</span>`;
  }
  return `<span class="route-opt__fuelgap">${PUMP_SVG}
    ${t('ptt.gaps', {
      n: g.count,
      avg: formatDistance(g.avgGapM, units),
      max: formatDistance(g.maxGapM, units),
    })}</span>`;
}

function routeStatsRow(st: RouteStats, atLights = 0): string {
  const bits: string[] = [];
  if (atLights > 60) {
    bits.push(
      `<span class="route-stat route-stat--lights">${t('stats.atLights', { d: formatDuration(atLights) })}</span>`,
    );
  }
  if (st.trafficSignals > 0) {
    bits.push(
      `<span class="route-stat"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="3" width="8" height="15" rx="2.4" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="7.5" r="1.5" fill="currentColor"/><circle cx="12" cy="14" r="1.5" fill="currentColor" opacity="0.45"/><path d="M12 18 V21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>${t(
        'stats.signals',
        { n: st.trafficSignals },
      )}</span>`,
    );
  }
  if (st.turns > 0) bits.push(`<span class="route-stat">${t('stats.turns', { n: st.turns })}</span>`);
  if (st.tollBooths > 0) bits.push(`<span class="route-stat">${t('stats.tolls', { n: st.tollBooths })}</span>`);
  if (!bits.length) return '';
  return `<span class="route-opt__stats">${bits.join('')}</span>`;
}

const FERRY_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 16 L5 11 h14 l1 5 M7 11 V7 h10 v4 M10 7 V5 h4 v2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M2 19 c1.6 1.4 3.4 1.4 5 0 c1.6 1.4 3.4 1.4 5 0 c1.6 1.4 3.4 1.4 5 0 c1.6 1.4 3.4 1.4 5 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const PUMP_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20 V5 a1.5 1.5 0 0 1 1.5-1.5 h5 A1.5 1.5 0 0 1 13 5 v15 M3.5 20 h11 M6.8 6 h4.4 v4 H6.8 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M13 9 h2.2 a1.5 1.5 0 0 1 1.5 1.5 V17 a1.4 1.4 0 0 0 2.8 0 V8.5 L17 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const TRAFFIC_SVG = `<svg viewBox="0 0 24 24" class="chip__icon" aria-hidden="true"><rect x="3" y="12" width="7" height="5" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.8"/><rect x="14" y="12" width="7" height="5" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.8"/><rect x="8.5" y="4.5" width="7" height="5" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M4.5 20 h15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const GO_SVG = `<svg viewBox="0 0 24 24" class="btn__icon"><path d="M3 11 L21 3 L13 21 L11 13 Z" fill="currentColor"/></svg>`;
const STAR_SVG = `<svg viewBox="0 0 24 24"><path d="M12 3 L14.7 8.6 L20.9 9.4 L16.4 13.7 L17.5 19.8 L12 16.9 L6.5 19.8 L7.6 13.7 L3.1 9.4 L9.3 8.6 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>`;
const CAR_SVG = `<svg viewBox="0 0 24 24" class="chip__icon"><path d="M5 11 L6.5 6.5 C6.8 5.6 7.6 5 8.5 5 h7 c.9 0 1.7.6 2 1.5 L19 11 M4 11 h16 v5 a1 1 0 0 1 -1 1 h-1 a1 1 0 0 1 -1 -1 v-1 H7 v1 a1 1 0 0 1 -1 1 H5 a1 1 0 0 1 -1 -1 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const TRUCK_SVG = `<svg viewBox="0 0 24 24" class="chip__icon"><path d="M2 6 h11 v9 H2 Z M13 9 h4 l3 3 v3 h-7 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="6" cy="17.5" r="1.8" fill="currentColor"/><circle cx="17" cy="17.5" r="1.8" fill="currentColor"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
